import {
  AxiosResponseAndError,
  ResponseType,
} from "../types/axiosResponseType";
import { QUERY_KEYS } from "./querykeys";
import ApiRequest from "../ApiRequest";
import { requestURLs } from "@/Environment";
import { useQuery } from "@tanstack/react-query";
import {
  OrgStatusEnumValue,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { TaskColorPaletteEnum } from "@backend/src/schemas/userSchema";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";
import { AssignedUsers } from "./useProjectQuery";

export type UserOrganisationType = {
  userOrganisationId: string;
  jobTitle?: string;
  role?: keyof typeof UserRoleEnumValue;
  taskColour?: TaskColorPaletteEnum;
  user: {
    userId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarImg?: string;
  };
};

export type OrganisationResponseType = ResponseType<{
  organisationId: string;
  organisationName: string;
  industry: string;
  status: keyof typeof OrgStatusEnumValue;
  country: string;
  nonWorkingDays: OrgListOfNonWorkingDaysEnum[];
  createdAt: Date;
  updatedAt: Date;
  tenantId: string;
  createdBy: string;
  userOrganisation: UserOrganisationType[];
}>;

export interface ProjectManagersProjects {
  isActive?: boolean;
  assignedUsers: AssignedUsers[];
  projectId: string;
  organisationId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  status: string;
  defaultView: string;
  timeTrack: any;
  budgetTrack: any;
  currency: string;
  overallTrack: string;
  estimatedBudget: string;
  actualCost: any;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  spi: number | null;
  progressionPercentage?: number | string | null;
  reCalculateEndDate?: string | null;
  projectActualStartDate?: string | null;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  portfolio?: {
    portfolioId: string;
    portfolioName: string;
    assignedUsers: {
      portfolioRole: string;
      user: {
        userId: string;
        email: string;
        firstName?: string;
        lastName?: string;
        avatarImg?: string;
      };
    }[];
  };
  program?: {
    programId: string;
    programName: string;
    assignedUsers: {
      programRole: string;
      user: {
        userId: string;
        email: string;
        firstName?: string;
        lastName?: string;
        avatarImg?: string;
      };
    }[];
  };
}
export interface Project {
  projectId: string;
  organisationId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  status: string;
  defaultView: string;
  timeTrack: any;
  budgetTrack: any;
  currency: string;
  overallTrack: string;
  estimatedBudget: string;
  actualCost: any;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  ganttColor?: string | null;
}

export interface StatusChartData {
  labels: string[];
  data: number[];
}

export interface OverallSituationChartData {
  labels: string[];
  data: number[];
}

export interface SpiChartData {
  labels: string[];
  data: number[];
}
export interface CpiChartData {
  CPILabels: string[];
  CPIdata: number[];
}

export type maangerPortfolioDashboardResponseType = ResponseType<{
  projects: ProjectManagersProjects[];
  statusChartData: StatusChartData;
  overallSituationChartData: OverallSituationChartData;
  spiData: SpiChartData;
  portfolioPercentage: number;
  portfolioPercentageData: { actualProgress: number; expectedProgress: number };
}>;

export type managerDashboardPortfolioDataType = {
  projects: ProjectManagersProjects[];
  statusChartData: StatusChartData;
  overallSituationChartData: OverallSituationChartData;
  spiData: SpiChartData;
  portfolioPercentage: number;
  portfolioPercentageData: { actualProgress: number; expectedProgress: number };
  CPIDataResponse?: CpiChartData;
};

function useProjectManagerPortfolioDashboardQuery(data: {
  programId: string;
  portfolioId: string;
},enabled=true) {
  return useQuery<
    AxiosResponseAndError<maangerPortfolioDashboardResponseType>["response"],
    AxiosResponseAndError<maangerPortfolioDashboardResponseType>["error"]
  >({
    queryKey: [QUERY_KEYS.portfolioDashboard, data.portfolioId, data.programId],
    queryFn: async ({ signal }) =>
      await ApiRequest.get(
        `${requestURLs.managerPortfolioDashboardData}?${new URLSearchParams(
          data,
        ).toString()}`,
        { signal },
      ),
    enabled: enabled,
  });
}

export default useProjectManagerPortfolioDashboardQuery;
